import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { forwardRef } from '@angular/core';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [NgSelectComponent, FormsModule, CommonModule],
  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
})
export class SelectComponent implements ControlValueAccessor {
  @ViewChild(NgSelectComponent) ngSelectComponent?: NgSelectComponent;

  @Input() items: any[] = [];
  @Input() bindLabel: string = 'label';
  @Input() bindValue: string = 'value';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() multiple: boolean = false;
  @Input() dropdownPosition: 'auto' | 'top' | 'bottom' = 'auto';
  @Input() clearable: boolean = false;
  @Input() searchable: boolean = true;

  @Input() usePipe: boolean = false;
  @Input() pipeType: 'date' | 'uppercase' | 'lowercase' | 'currency' = 'date';
  @Input() pipeFormat: string = 'dd/MM/yyyy';
  @Input() locale: string = 'en-US';
  @Input() customStyle: boolean = false;
  @Input() appendTo?: string;
  @Input() existingItems?: string[] = [];

  @Input() set selected(value: any) {
    this._selected = value;
    if (!value) {
      this.selectedChange.emit(null);
    }
  }

  get selected() {
    return this._selected;
  }

  @Output() selectedChange = new EventEmitter<any | string | string[] | null>();

  private _selected: any;

  displayedItems: any[] = [];
  currentSearchTerm: string = '';

  // Custom search function that prioritizes items starting with search term
  customSearchFn = (term: string, item: any): boolean => {
    if (!term) return true;

    const searchTerm = term.toLowerCase();
    const itemLabel = (item[this.bindLabel] || '').toString().toLowerCase();

    return itemLabel.includes(searchTerm);
  };

  constructor(private datepipe: DatePipe) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['existingItems']) {
      this.updateDisplayedItems();
    }
  }

  onChange(value: any) {
    let finalValue: any = null;

    if (!value && value !== 0) {
      finalValue = null;
    } else if (this.multiple && Array.isArray(value)) {
      finalValue = value.map((v) =>
        typeof v === 'object' ? (v.id ?? v[this.bindValue]) : v,
      );
    } else if (typeof value !== 'object') {
      finalValue = value;
    } else {
      finalValue = value.id ?? value[this.bindValue];
    }

    this.selectedChange.emit(finalValue);
    this._onChangedCallback(finalValue);
  }

  private updateDisplayedItems(): void {
    if (!this.items) {
      this.displayedItems = [];
      return;
    }

    let items = this.items.map((s) => ({
      ...s,
      disabled: this.existingItems?.some(
        (x) => x.toString() === (s.id ?? s[this.bindValue]).toString(),
      ),
    }));

    // Sort by search relevance if there's a search term
    if (this.currentSearchTerm) {
      const searchTerm = this.currentSearchTerm.toLowerCase();
      items = items.sort((a, b) => {
        const aLabel = (a[this.bindLabel] || '').toString().toLowerCase();
        const bLabel = (b[this.bindLabel] || '').toString().toLowerCase();

        const aStartsWith = aLabel.startsWith(searchTerm);
        const bStartsWith = bLabel.startsWith(searchTerm);

        // Items starting with search term come first
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        // If both start with or both don't start with, maintain alphabetical order
        return aLabel.localeCompare(bLabel);
      });
    }

    this.displayedItems = items;
  }

  onSearch(term: string): void {
    this.currentSearchTerm = term;
    this.updateDisplayedItems();
  }

  onClear() {
    this.currentSearchTerm = '';
    this.updateDisplayedItems();
    this.selectedChange.emit(null);
  }

  onClose() {
    this.currentSearchTerm = '';
    this.updateDisplayedItems();
  }

  get transformedItems(): any[] {
    if (!this.usePipe || !this.items?.length) {
      return this.items;
    }

    return this.items.map((item) => {
      const transformedItem = { ...item };
      if (transformedItem[this.bindLabel]) {
        transformedItem[this.bindLabel] = this.applyPipe(
          transformedItem[this.bindLabel],
        );
      }

      return transformedItem;
    });
  }

  private applyPipe(value: any): any {
    if (!value) return value;

    switch (this.pipeType) {
      case 'date':
        return this.datepipe.transform(
          value,
          this.pipeFormat,
          undefined,
          this.locale,
        );
      case 'uppercase':
        return value.toString().toUpperCase();
      case 'lowercase':
        return value.toString().toLowerCase();
      case 'currency':
        return value;
      default:
        return value;
    }
  }

  private onTouched = () => {};

  private _onChangedCallback = (_: any) => {};
  private _onTouchedCallback = () => {};

  writeValue(value: any): void {
    this._selected = value;
    // Explicitly clear the ng-select component when value is null
    if (!value && value !== 0 && this.ngSelectComponent) {
      this.ngSelectComponent.handleClearClick();
    }
  }

  registerOnChange(fn: any): void {
    this._onChangedCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn;
  }

  handleChange(value: any) {
    this.onChange(value);
  }

  handleTouched() {
    this._onTouchedCallback();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
